/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export const researchTaskCopy = {
  en: {
    title: 'Deep Research',
    task: 'Task ID',
    sources: 'Sources',
    billingPending:
      'The report is complete, but provider usage was unavailable. Credit settlement needs administrator reconciliation.',
    failed: 'Research failed',
    cancelled: 'Research cancelled',
    interrupted: 'Research interrupted',
    liveReport: 'Live report draft',
    reportUpdated: 'Last report update:',
    reportNotUpdated: 'Not yet',
    queuedStatus: 'Queued for research',
    waitingApprovalStatus: 'Waiting for plan approval',
    approvedWaitingStatus: 'Approved plan waiting to start',
    researchingStatus: 'Researching',
    cancellingStatus: 'Cancelling research',
    cancellationUnconfirmed:
      'The provider has not confirmed cancellation. We will keep checking the task status.',
    submissionUnknown:
      'The task could not be confirmed with the research provider. Contact support before retrying.',
    reconcilingStatus: 'Checking research status',
    partialStatus: 'Research complete with partial coverage',
    completedStatus: 'Research complete',
    expiredStatus: 'Research task expired',
    statusUpdating: 'Research status is updating',
    planningStage: 'Preparing the research plan',
    awaitingPlanStage: 'Review the plan to start research',
    startingStage: 'Starting approved research',
    submittingStage: 'Sending research request',
    researchingStage: 'Researching sources',
    searchingStage: 'Searching public sources',
    readingStage: 'Reading sources',
    assessingStage: 'Assessing evidence',
    writingStage: 'Writing the report',
    verifyingStage: 'Checking report citations',
    cancel: 'Cancel research',
    delete: 'Delete research task',
    deleting: 'Deleting…',
    deleted: 'Research task deleted',
    preview:
      'Preview: use only a standalone public, nonsensitive question. Do not include conversation history or attachments; they are not approved Preview inputs.',
    login: 'Login required to view research',
    unavailable: 'Research task unavailable',
  },
  'zh-CN': {
    title: '深度研究',
    task: '任务编号',
    sources: '来源',
    billingPending:
      '报告已完成，但服务商未返回用量数据。Credit 结算需要管理员进行核对。',
    failed: '研究失败',
    cancelled: '研究已取消',
    interrupted: '研究已中断',
    liveReport: '实时研究报告草稿',
    reportUpdated: '报告最近更新：',
    reportNotUpdated: '暂无更新',
    queuedStatus: '等待研究开始',
    waitingApprovalStatus: '等待你批准研究计划',
    approvedWaitingStatus: '研究计划已批准，等待开始',
    researchingStatus: '正在研究',
    cancellingStatus: '正在取消研究',
    cancellationUnconfirmed: '服务商尚未确认取消。我们会继续检查任务状态。',
    submissionUnknown:
      '无法确认服务商是否已收到此任务。请联系支持人员后再重试。',
    reconcilingStatus: '正在确认研究状态',
    partialStatus: '研究已完成，报告覆盖不完整',
    completedStatus: '研究完成',
    expiredStatus: '研究任务已过期',
    statusUpdating: '研究状态正在更新',
    planningStage: '正在准备研究计划',
    awaitingPlanStage: '请审核计划以开始研究',
    startingStage: '正在启动已批准的研究',
    submittingStage: '正在提交研究请求',
    researchingStage: '正在研究资料',
    searchingStage: '正在搜索公开资料',
    readingStage: '正在阅读资料',
    assessingStage: '正在评估证据',
    writingStage: '正在撰写报告',
    verifyingStage: '正在检查报告引用',
    cancel: '取消研究',
    delete: '删除研究任务',
    deleting: '正在删除…',
    deleted: '研究任务已删除',
    preview:
      '预览版：仅使用独立、公开且不敏感的问题。请勿包含对话历史或附件；这些内容不属于已批准的预览版输入。',
    login: '登录后查看研究',
    unavailable: '研究任务不可用',
  },
  'zh-TW': {
    title: '深度研究',
    task: '任務編號',
    sources: '來源',
    billingPending:
      '報告已完成，但服務商未返回用量資料。Credit 結算需要管理員進行核對。',
    failed: '研究失敗',
    cancelled: '研究已取消',
    interrupted: '研究已中斷',
    liveReport: '即時研究報告草稿',
    reportUpdated: '報告最近更新：',
    reportNotUpdated: '尚無更新',
    queuedStatus: '等待研究開始',
    waitingApprovalStatus: '等待你核准研究計畫',
    approvedWaitingStatus: '研究計畫已核准，等待開始',
    researchingStatus: '正在研究',
    cancellingStatus: '正在取消研究',
    cancellationUnconfirmed: '服務商尚未確認取消。我們會繼續檢查任務狀態。',
    submissionUnknown:
      '無法確認服務商是否已收到此任務。請聯絡支援人員後再重試。',
    reconcilingStatus: '正在確認研究狀態',
    partialStatus: '研究已完成，報告覆蓋不完整',
    completedStatus: '研究完成',
    expiredStatus: '研究任務已過期',
    statusUpdating: '研究狀態正在更新',
    planningStage: '正在準備研究計畫',
    awaitingPlanStage: '請審核計畫以開始研究',
    startingStage: '正在啟動已核准的研究',
    submittingStage: '正在提交研究請求',
    researchingStage: '正在研究資料',
    searchingStage: '正在搜尋公開資料',
    readingStage: '正在閱讀資料',
    assessingStage: '正在評估證據',
    writingStage: '正在撰寫報告',
    verifyingStage: '正在檢查報告引用',
    cancel: '取消研究',
    delete: '刪除研究任務',
    deleting: '正在刪除…',
    deleted: '研究任務已刪除',
    preview:
      '預覽版：僅使用獨立、公開且不敏感的問題。請勿包含對話歷史或附件；這些內容不屬於已核准的預覽版輸入。',
    login: '登入後查看研究',
    unavailable: '研究任務無法使用',
  },
};

export type ResearchTaskLabels = (typeof researchTaskCopy)['en'];

interface StatusDefinition {
  label: keyof ResearchTaskLabels;
  terminal?: boolean;
}

const statusDefinitions: Record<string, StatusDefinition> = {
  queued: { label: 'queuedStatus' },
  waiting_authorization: { label: 'waitingApprovalStatus' },
  waiting_for_approval: { label: 'waitingApprovalStatus' },
  researching: { label: 'researchingStatus' },
  in_progress: { label: 'researchingStatus' },
  running: { label: 'researchingStatus' },
  cancelling: { label: 'cancellingStatus' },
  reconciling: { label: 'reconcilingStatus' },
  recovering: { label: 'reconcilingStatus' },
  delayed: { label: 'reconcilingStatus' },
  completed: { label: 'completedStatus', terminal: true },
  failed: { label: 'failed', terminal: true },
  cancelled: { label: 'cancelled', terminal: true },
  canceled: { label: 'cancelled', terminal: true },
  interrupted: { label: 'interrupted', terminal: true },
  expired: { label: 'expiredStatus', terminal: true },
  deleted: { label: 'deleted', terminal: true },
};

const stageLabelKeys: Record<string, keyof ResearchTaskLabels> = {
  plan_pending: 'planningStage',
  planning: 'planningStage',
  awaiting_plan: 'awaitingPlanStage',
  approved_pending_admission: 'startingStage',
  submitting: 'submittingStage',
  researching: 'researchingStage',
  searching: 'searchingStage',
  reading: 'readingStage',
  assessing: 'assessingStage',
  writing: 'writingStage',
  verifying: 'verifyingStage',
  cancelling: 'cancellingStatus',
  submission_unknown: 'reconcilingStatus',
  release_pending: 'reconcilingStatus',
};

export function statusLabel(
  status: string,
  labels: ResearchTaskLabels,
  context: { stage?: string; coverage?: string } = {},
): string {
  if (
    status === 'waiting_authorization' &&
    context.stage === 'approved_pending_admission'
  ) {
    return labels.approvedWaitingStatus;
  }
  if (status === 'completed' && context.coverage === 'partial') {
    return labels.partialStatus;
  }
  const definition = statusDefinitions[status];
  return definition ? labels[definition.label] : labels.statusUpdating;
}

export function isTerminalResearchTaskStatus(status: string): boolean {
  return statusDefinitions[status]?.terminal === true;
}

export function stageLabel(
  stage: string | undefined,
  labels: ResearchTaskLabels,
): string {
  const key = stage ? stageLabelKeys[stage] : undefined;
  return key ? labels[key] : '';
}

export function terminalStatusLabel(
  status: string,
  labels: ResearchTaskLabels,
): string | undefined {
  const definition = statusDefinitions[status];
  return definition?.terminal ? labels[definition.label] : undefined;
}
